<script setup>
import Icon from 'next/icon/Icon.vue';

defineProps({
  channel: { type: Object, required: true },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);
</script>

<template>
  <button
    type="button"
    class="group relative flex w-full flex-col gap-6 rounded-2xl border border-n-weak bg-n-solid-1 p-5 text-left shadow-sm outline-none transition-all duration-200 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-n-solid-1 sm:p-6 enabled:hover:border-[var(--channel-border)] enabled:hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
    :style="{
      '--channel-border': `${channel.color}66`,
      '--tw-ring-color': channel.color,
    }"
    :disabled="disabled"
    :aria-label="channel.ctaLabel"
    @click="emit('select', channel.key)"
  >
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
      <span
        class="flex size-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm"
        :style="{ backgroundColor: channel.color }"
      >
        <Icon :icon="channel.icon" class="size-7" />
      </span>

      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-2">
          <h3 class="text-xl font-semibold tracking-tight text-n-slate-12">
            {{ channel.title }}
          </h3>
          <span
            v-if="channel.recommended"
            class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
            :style="{ backgroundColor: channel.color }"
          >
            <Icon icon="i-hugeicons-star" class="size-3" />
            {{ channel.recommended }}
          </span>
          <span
            v-if="channel.badge"
            class="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold text-n-slate-12"
            :style="{ borderColor: `${channel.color}80` }"
          >
            <Icon
              icon="i-hugeicons-checkmark-circle-02"
              class="size-3.5"
              :style="{ color: channel.color }"
            />
            {{ channel.badge }}
          </span>
        </div>
        <p class="mt-1.5 max-w-2xl text-sm leading-6 text-n-slate-11">
          {{ channel.description }}
        </p>
      </div>
    </div>

    <div v-if="channel.options?.length" class="flex flex-col gap-3">
      <div>
        <p class="text-sm font-semibold text-n-slate-12">
          {{ channel.optionsTitle }}
        </p>
        <p v-if="channel.optionsHint" class="mt-0.5 text-xs text-n-slate-11">
          {{ channel.optionsHint }}
        </p>
      </div>
      <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div
          v-for="option in channel.options"
          :key="option.title"
          class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <span
              class="flex size-10 shrink-0 items-center justify-center rounded-xl"
              :style="{
                backgroundColor: `${channel.color}1A`,
                color: channel.color,
              }"
            >
              <Icon :icon="option.icon" class="size-5" />
            </span>
            <span
              v-if="option.tag"
              class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-n-slate-11"
            >
              {{ option.tag }}
            </span>
          </div>
          <div class="min-w-0">
            <p class="text-sm font-semibold text-n-slate-12">
              {{ option.title }}
            </p>
            <p class="mt-1 text-[13px] leading-5 text-n-slate-11">
              {{ option.description }}
            </p>
          </div>
          <p
            v-if="option.ideal"
            class="mt-auto flex items-start gap-1.5 border-t border-n-weak pt-3 text-xs leading-5 text-n-slate-12"
          >
            <Icon
              icon="i-hugeicons-checkmark-circle-02"
              class="mt-0.5 size-3.5 shrink-0"
              :style="{ color: channel.color }"
            />
            {{ option.ideal }}
          </p>
        </div>
      </div>
    </div>

    <div
      class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <p class="flex items-center gap-2 text-xs text-n-slate-11">
        <Icon icon="i-hugeicons-idea-01" class="size-4 shrink-0" />
        {{ channel.footnote }}
      </p>
      <span
        class="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-white shadow-sm transition-[filter] group-enabled:group-hover:brightness-110"
        :style="{ backgroundColor: channel.color }"
      >
        {{ channel.ctaLabel }}
        <Icon
          icon="i-hugeicons-arrow-right-01"
          class="size-4 transition-transform group-enabled:group-hover:translate-x-0.5 motion-reduce:transition-none"
        />
      </span>
    </div>
  </button>
</template>
